import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import type { PatientOut } from '../api/patients-api';
import { useUpdatePatient } from '../api/patients-api';
import { PatientFormFields } from './PatientFormFields';
import {
  formValuesToMetadata,
  patientFormSchema,
  type PatientFormValues,
  patientToFormValues,
} from '../schemas/patientForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EditPatientDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patient: PatientOut;
}

export function EditPatientDialog({ open, patient, onOpenChange }: EditPatientDialogProps) {
  const updatePatient = useUpdatePatient();
  const initialValues = useMemo(() => patientToFormValues(patient), [patient]);
  const form = useForm<PatientFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(patientFormSchema),
  });

  // Re-seed the form only on the closed→open transition, so an updated
  // `patient` (e.g. a background refetch) can't reset the form mid-edit and
  // discard unsaved input.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) form.reset(initialValues);
    wasOpen.current = open;
  }, [open, initialValues, form]);

  const onSubmit = async (values: PatientFormValues) => {
    try {
      await updatePatient.mutateAsync({
        patientId: patient.id,
        metadata_: formValuesToMetadata(values, patient.metadata_),
      });
      toast.success('Patient updated');
      onOpenChange(false);
    } catch {
      toast.error('Failed to update patient'); // generic — no PHI
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0 sm:max-h-[85vh]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Edit Patient</DialogTitle>
          <DialogDescription>
            Update this patient's details. The medical record number cannot be changed.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="flex min-h-0 flex-1 flex-col" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <PatientFormFields disableMrn form={form} />
            </div>

            <DialogFooter className="border-t px-6 py-4">
              <Button
                type="button"
                variant="ghost"
                disabled={updatePatient.isPending}
                onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updatePatient.isPending}>
                {updatePatient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
